import './orderItem.css';
import React, { useState } from 'react';
import { mergePDF, formatDate, downloadFiles } from '../../../../../util/util';
import { handleError } from '../../../../../model/notification';
import { TableBlock } from '../../../../../components/TableBlock/TableBlock';
import classnames from 'classnames';
import { Icon } from '../../../../../components/Icon/Icon';
import { t } from '../../../../../locales/translate';
import { useRemoteData } from '../../../../../model/data';
import { getEventDetails } from '../../../../../model/event';
import { cacheRequest } from '../../../../../model/cache';
import { getProductName, Product } from '../../../../../model/product';
import { compact } from 'lodash';
import { Invoice } from '../../../../../model/invoice';
import { Ticket } from '../../../../../model/order';

async function downloadTickets(tickets: string[], setLoading: (loading: boolean) => void): Promise<void> {
  setLoading(true);
  try {
    await downloadFiles([
      {
        blob: await mergePDF(tickets),
        filename: 'tickets',
      },
    ]);
  } catch (error) {
    handleError(error);
  }
  setLoading(false);
}

function downloadTicketAction(
  tickets: Ticket[] | undefined,
  invoice: Invoice | undefined,
  ticketDownload: boolean,
  loading: boolean,
  setLoading: (loading: boolean) => void,
  isPollingTicketUrls: boolean
): JSX.Element | null {
  if (!tickets?.length) return null;
  if (invoice?.status !== 'booked') return null;

  const ticketsUrls = compact(tickets.map((t) => t.url) || []);
  const allTicketsHaveUrl = ticketsUrls.length === tickets.length;
  if (!isPollingTicketUrls && !allTicketsHaveUrl) return null;

  const isActuallyLoading = loading || (isPollingTicketUrls && !allTicketsHaveUrl);
  if (ticketDownload) {
    return (
      <Icon
        name="download--sm"
        loading={isActuallyLoading}
        onClick={() => {
          if (!isActuallyLoading) {
            downloadTickets(ticketsUrls, setLoading);
          }
        }}
      />
    );
  }

  const icon = <Icon name="external-link" loading={isActuallyLoading} />;
  return isActuallyLoading ? (
    icon
  ) : (
    <a href={ticketsUrls[0]} target="_blank" rel="noopener noreferrer">
      {icon}
    </a>
  );
}

interface Props {
  invoice?: Invoice;
  product?: Partial<Product>;
  displayName?: JSX.Element;
  tickets?: Ticket[];
  invalidated?: boolean;
  ticketDownload?: boolean;
  isPollingTicketUrls: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export function ProductCell({
  invoice,
  product,
  displayName,
  tickets,
  invalidated,
  onMouseEnter,
  onMouseLeave,
  ticketDownload = true,
  isPollingTicketUrls,
}: Props): JSX.Element {
  const [loading, setLoading] = useState(false);

  if (!product) {
    return <div></div>;
  }

  return (
    <div onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <TableBlock
        className={classnames({
          'order-details__invalidated-product': invalidated,
        })}
        header={displayName || getProductName(product)}
        footer={product?.__typename === 'Event' && <EventItemDetails product={product} />}
        icon={
          <div className="product-cell__actions">
            {downloadTicketAction(tickets, invoice, ticketDownload, loading, setLoading, isPollingTicketUrls)}
          </div>
        }
      />
    </div>
  );
}

function EventItemDetails({ product }: { product: Partial<Product> }): JSX.Element {
  const [event, loading, error] = useRemoteData(
    async () =>
      await cacheRequest(`getEventDetails_${product?.id}_false_false`, () =>
        getEventDetails({
          eventId: product?.id || '',
          includeCategories: false,
        })
      ),
    (db) => {
      return (
        db.table('events').where({ slug: product?.slug ?? undefined })[0] ||
        db.table('events').where({ id: product?.id })[0]
      );
    }
  );

  if (loading) {
    return <div>{t.actions.loading(t.model.event.eventDetails)}...</div>;
  } else if (!event || error) {
    return <div>{t.errors.errorLoadingItem(t.model.event.eventDetails)}</div>;
  } else {
    return (
      <div className="detail-block">
        <span>
          {[formatDate(event.happeningFrom), [event.venue?.name, event.venue?.address.city].filter(Boolean).join(', ')]
            .filter(Boolean)
            .join(' - ')}
        </span>
      </div>
    );
  }
}
